/* Data-driven SEO landing pages. Each entry renders via pages/SeoLanding.js */

export const LINKTREE_ROWS = [
  { label: 'Free plan', us: 'Yes — forever', them: 'Yes' },
  { label: 'Pro price', us: '€4/mo · €3.33/mo billed yearly', them: 'From ~$5–9/mo' },
  { label: 'Real brand icons', us: '63 included, official colours', them: 'Limited set' },
  { label: 'Rich embeds (Spotify, YouTube, SoundCloud…)', us: 'Auto-detected, inline players', them: 'Partly gated' },
  { label: 'Tip jar', us: 'Direct PayPal — no commission taken', them: 'Platform fees apply' },
  { label: 'Fan page feed', us: 'Bebo-style posts + photos', them: 'Not available' },
  { label: 'Visitor analytics + country map', us: 'Included', them: 'Higher tiers' },
  { label: 'QR code generator (PNG + SVG)', us: 'Included', them: 'Paid tier' },
  { label: 'Themes, fonts & wallpapers', us: '17 themes · 12 fonts · 11 wallpapers', them: 'Limited on free' },
  { label: 'Timed links — auto show/hide on your page', us: 'Included with Pro', them: 'Paid tier' },
  { label: 'Email subscribers + campaigns', us: 'Built in', them: 'Limited' },
];

const CORE_FEATURES = [
  { icon: 'sparkles', title: '63 real brand icons', text: 'Official Spotify green, real TikTok gradient — not generic glyphs.' },
  { icon: 'music', title: 'Rich embeds', text: 'YouTube, Spotify, SoundCloud, Apple Music and more play inline on your page.' },
  { icon: 'heart', title: 'Tip jar', text: 'Fans tip you directly via PayPal. LinkMonkey takes no cut.' },
  { icon: 'chart', title: 'Analytics', text: 'Clicks, views and a country-level visitor map, included.' },
  { icon: 'qrcode', title: 'QR codes', text: 'Branded QR codes in PNG + SVG for flyers, merch and gigs.' },
  { icon: 'palette', title: 'Full customisation', text: '17 themes, 12 fonts, 11 wallpapers, animations and custom accents.' },
];

export const seoPages = [
  {
    slug: 'linktree-alternative',
    title: 'LinkMonkey — The Affordable Linktree Alternative for Creators',
    metaDescription: 'Looking for a Linktree alternative? LinkMonkey gives you rich embeds, tip jar, analytics, QR codes and 63 brand icons — free to start, Pro from €3.33/mo.',
    eyebrow: 'Linktree alternative',
    h1: ['Everything Linktree charges for.', 'Included.'],
    sub: 'LinkMonkey is the affordable Linktree alternative for creators: unlimited links, rich embeds, real brand icons, tip jar, fan page, analytics and QR codes — free to start, Pro from €3.33/mo.',
    bullets: ['Free plan forever', 'Pro from €3.33/mo', 'No commission on tips', '2-minute switch'],
    features: CORE_FEATURES,
    comparison: { competitor: 'Linktree', rows: LINKTREE_ROWS },
    faqs: [
      { q: 'How is LinkMonkey different from Linktree?', a: 'LinkMonkey includes the features Linktree gates behind higher tiers — rich embeds, analytics, QR codes, themes and a tip jar — on one simple plan. Pro is €4/mo or €40/yr (€3.33/mo), and there is a free plan forever.' },
      { q: 'Can I move my links from Linktree to LinkMonkey?', a: 'Yes. Create a free account, add your links (each takes seconds with auto-detected platforms), and swap the URL in your Instagram or TikTok bio. Most creators switch in under 5 minutes.' },
      { q: 'Does LinkMonkey take a cut of my tips?', a: 'No. The tip jar pays out directly to your PayPal. LinkMonkey takes no commission.' },
      { q: 'Is there a free plan?', a: 'Yes — free forever for the essentials. Pro unlocks everything for €4/mo, or €40/yr (€3.33/mo). There is also a 7-day Pro trial with no card required.' },
      { q: 'Will my page work on Instagram, TikTok and YouTube?', a: 'Yes. Your LinkMonkey URL (linkmonkey.io/yourname) works in any bio field on any platform, and every page is mobile-first.' },
    ],
    cta: { headline: 'Switch from Linktree in minutes.', sub: 'Claim your username before someone else does.' },
  },
  {
    slug: 'free-link-in-bio',
    title: 'Free Link in Bio Page — LinkMonkey',
    metaDescription: 'Create a free link-in-bio page with unlimited links, real brand icons and a beautiful mobile profile. No card required. Pro upgrades from €3.33/mo.',
    eyebrow: 'Free link in bio',
    h1: ['A free link in bio', 'that actually looks good.'],
    sub: 'Create your page in two minutes: unlimited links, real brand icons, mobile-first design and a shareable linkmonkey.io/yourname URL — completely free, no card required.',
    bullets: ['Free forever', 'No card required', 'Unlimited links', '7-day Pro trial included'],
    features: [
      { icon: 'zap', title: 'Live in 2 minutes', text: 'Sign up, add your links, share your URL. Done.' },
      { icon: 'sparkles', title: '63 real brand icons', text: 'Every platform you are on, with its real colours.' },
      { icon: 'layers', title: 'Live preview', text: 'Edit on the left, watch your page update in real time.' },
      { icon: 'globe', title: 'Your own URL', text: 'linkmonkey.io/yourname — claim it before someone else does.' },
      { icon: 'chart', title: 'See what free gets you', text: 'Core links, icons and profile customisation, free forever.' },
      { icon: 'euro', title: 'Upgrade only if you want', text: 'Pro adds embeds, tip jar, analytics map, QR codes and more from €3.33/mo.' },
    ],
    faqs: [
      { q: 'Is LinkMonkey really free?', a: 'Yes. The free plan is free forever and includes your public page, unlimited links and brand icons. No card required to sign up.' },
      { q: 'What does Pro add?', a: 'Pro unlocks rich embeds, tip jar, fan page, full analytics with country map, QR codes, timed links (auto show/hide on your page), all themes and layouts — €4/mo or €40/yr (€3.33/mo).' },
      { q: 'Can I try Pro before paying?', a: 'Yes — every new account gets a 7-day Pro trial, no card required.' },
      { q: 'Can I use my free page on Instagram and TikTok?', a: 'Yes, your linkmonkey.io/yourname URL works in any bio on any platform.' },
    ],
    cta: { headline: 'Create your free page now.', sub: 'No card. No catch. Live in two minutes.' },
  },
  {
    slug: 'link-in-bio-for-musicians',
    title: 'Link in Bio for Musicians — LinkMonkey',
    metaDescription: 'A link-in-bio page built for musicians: Spotify, SoundCloud and Apple Music embeds, tip jar for fan support, tour links, merch and QR codes for gigs.',
    eyebrow: 'For musicians',
    h1: ['Your music, merch and shows.', 'One link.'],
    sub: 'Embed Spotify, SoundCloud, Apple Music and YouTube directly on your page. Add tour dates, merch, a tip jar for fan support and a QR code for the merch table.',
    bullets: ['Inline music players', 'Tip jar — no commission', 'QR codes for gigs', 'Fan page for updates'],
    features: [
      { icon: 'music', title: 'Music that plays inline', text: 'Spotify, SoundCloud, Apple Music, Bandcamp and YouTube embeds — fans listen without leaving your page.' },
      { icon: 'heart', title: 'Fan support built in', text: 'A tip jar that pays straight to your PayPal. Keep 100% of what fans send.' },
      { icon: 'qrcode', title: 'QR for the merch table', text: 'Print your branded QR on flyers, vinyl sleeves and posters.' },
      { icon: 'calendar', title: 'Release-day timed links', text: 'Set your pre-save and release links to appear on your page automatically on release day.' },
      { icon: 'mail', title: 'Grow your mailing list', text: 'Collect fan emails on your page and send campaigns when you drop.' },
      { icon: 'chart', title: 'Know your audience', text: 'See which cities click your tour link the most.' },
    ],
    example: { username: 'influencer1', label: 'See a live musician page' },
    faqs: [
      { q: 'Can fans play my music on my LinkMonkey page?', a: 'Yes — paste a Spotify, SoundCloud, Apple Music, Bandcamp or YouTube link and it becomes an inline player automatically.' },
      { q: 'How do fans tip me?', a: 'Enable the tip jar and fans pay directly to your PayPal. LinkMonkey takes no cut.' },
      { q: 'Can I promote a new single?', a: 'Yes — pin it to the top, embed the track, and even set the link to appear on your page automatically on release day.' },
      { q: 'How much does it cost?', a: 'Free to start. Pro (embeds, tip jar, analytics, QR codes) is €4/mo or €3.33/mo billed yearly.' },
    ],
    cta: { headline: 'Set the stage for your next release.', sub: 'Free to start — your fans are one link away.' },
  },
  {
    slug: 'link-in-bio-for-streamers',
    title: 'Link in Bio for Streamers — LinkMonkey',
    metaDescription: 'A link-in-bio page for Twitch and YouTube streamers: timed links, Discord invites, tip jar, clips embeds and real brand icons.',
    eyebrow: 'For streamers',
    h1: ['Twitch, YouTube, Discord.', 'One link for your community.'],
    sub: 'Send viewers to your streams, clips, Discord and socials from a single page — with a tip jar for direct viewer support and embeds for your best clips.',
    bullets: ['Twitch + Kick + YouTube icons', 'Discord invite front and centre', 'Tip jar — no commission', 'Clip embeds'],
    features: [
      { icon: 'zap', title: 'All your channels', text: 'Twitch, Kick, YouTube, Discord, X — real icons in real colours.' },
      { icon: 'music', title: 'Clip embeds', text: 'Showcase your best moments with inline YouTube embeds.' },
      { icon: 'heart', title: 'Direct viewer support', text: 'A tip jar that skips middlemen — straight to your PayPal.' },
      { icon: 'chart', title: 'See where viewers come from', text: 'Country-level analytics on every click.' },
      { icon: 'calendar', title: 'Timed stream links', text: 'Your "LIVE now" link can appear and disappear from your page automatically at set times.' },
      { icon: 'palette', title: 'Match your brand', text: 'Dark themes, neon accents, animated wallpapers.' },
    ],
    example: { username: 'influencer2', label: 'See a live streamer page' },
    faqs: [
      { q: 'Can I link Twitch, Kick and YouTube on one page?', a: 'Yes — LinkMonkey has real brand icons for 63 platforms including Twitch, Kick, YouTube, Discord and X.' },
      { q: 'Can viewers tip me?', a: 'Yes, through the built-in tip jar. Payments go directly to your PayPal with no commission.' },
      { q: 'Is it free?', a: 'Yes, free to start. Pro is €4/mo or €3.33/mo billed yearly.' },
    ],
    cta: { headline: 'Level up your bio link.', sub: 'Your community, one link away.' },
  },
  {
    slug: 'link-in-bio-for-artists',
    title: 'Link in Bio for Artists — LinkMonkey',
    metaDescription: 'A link-in-bio page for artists and illustrators: portfolio links, print shop, commissions, Instagram and Behance icons, tips from collectors.',
    eyebrow: 'For artists',
    h1: ['Your portfolio, prints', 'and commissions. One link.'],
    sub: 'Point followers to your shop, portfolio, commission sheet and socials from one beautiful page that matches your aesthetic — with 17 themes and custom wallpapers.',
    bullets: ['Behance + Dribbble icons', 'Print shop links', 'Tips from collectors', 'Custom aesthetics'],
    features: [
      { icon: 'palette', title: 'Make it yours', text: '17 themes, 12 fonts, 11 wallpapers and custom accent colours to match your art style.' },
      { icon: 'sparkles', title: 'Portfolio platforms', text: 'Instagram, Behance, Dribbble, Pinterest, DeviantArt — real icons included.' },
      { icon: 'heart', title: 'Tips from collectors', text: 'Fans support your work directly via PayPal tip jar.' },
      { icon: 'qrcode', title: 'QR for art fairs', text: 'Print a branded QR code for your booth and business cards.' },
      { icon: 'mail', title: 'Collector mailing list', text: 'Collect emails and announce new drops and commissions.' },
      { icon: 'chart', title: 'Know your audience', text: 'See which links and pieces get the most clicks.' },
    ],
    example: { username: 'influencer3', label: 'See a live artist page' },
    faqs: [
      { q: 'Can I link my print shop and commission sheet?', a: 'Yes — add unlimited links to Etsy, INPRNT, your own shop, Google Forms and anything else.' },
      { q: 'Can I customise the look?', a: 'Completely. Themes, fonts, wallpapers, animations, button styles and accent colours.' },
      { q: 'How much is it?', a: 'Free to start. Pro is €4/mo or €3.33/mo billed yearly.' },
    ],
    cta: { headline: 'Give your art a stage.', sub: 'A page as beautiful as your work.' },
  },
  {
    slug: 'link-in-bio-for-podcasters',
    title: 'Link in Bio for Podcasters — LinkMonkey',
    metaDescription: 'A link-in-bio page for podcasters: Spotify and Apple Podcasts embeds, latest-episode links, Patreon, listener tips and email campaigns.',
    eyebrow: 'For podcasters',
    h1: ['Every episode, every platform.', 'One link.'],
    sub: 'Embed your latest episode, link every listening platform, collect listener emails and take direct listener support — all from one page.',
    bullets: ['Spotify embeds', 'Latest-episode pin', 'Listener tips', 'Email campaigns'],
    features: [
      { icon: 'music', title: 'Episodes that play inline', text: 'Spotify and YouTube embeds — listeners press play right on your page.' },
      { icon: 'zap', title: 'Every platform, one page', text: 'Spotify, Apple Music, YouTube, Patreon and more with real brand icons.' },
      { icon: 'heart', title: 'Listener support', text: 'Direct PayPal tips from your audience, no middleman cut.' },
      { icon: 'mail', title: 'Own your audience', text: 'Collect emails on your page and send new-episode campaigns.' },
      { icon: 'calendar', title: 'Timed episode links', text: 'Set new-episode links to appear on your page automatically on publish day.' },
      { icon: 'chart', title: 'Listener insights', text: 'See which platforms and episodes your audience clicks.' },
    ],
    example: { username: 'influencer4', label: 'See a live podcaster page' },
    faqs: [
      { q: 'Can I embed my podcast?', a: 'Yes — Spotify and YouTube links become inline players automatically.' },
      { q: 'Can I collect listener emails?', a: 'Yes, the subscribe widget collects emails on your page and you can send campaigns from LinkMonkey.' },
      { q: 'What does it cost?', a: 'Free to start. Pro is €4/mo or €3.33/mo billed yearly.' },
    ],
    cta: { headline: 'One link for every listener.', sub: 'Wherever they listen, they start here.' },
  },
  {
    slug: 'link-in-bio-for-small-business',
    title: 'Link in Bio for Small Business — LinkMonkey',
    metaDescription: 'A simple mobile landing page for small businesses: menu, booking, order links, WhatsApp, opening hours, QR codes for the counter and reviews.',
    eyebrow: 'For small business',
    h1: ['A mobile landing page', 'without the web agency.'],
    sub: 'Put your menu, booking link, order page, WhatsApp and socials on one clean mobile page — with a QR code for the counter, window or receipts.',
    bullets: ['Live in minutes', 'QR for the counter', 'WhatsApp + socials', 'No web designer needed'],
    features: [
      { icon: 'zap', title: 'Live today', text: 'No agency, no CMS. Add your links and share — done before lunch.' },
      { icon: 'qrcode', title: 'QR everywhere', text: 'Branded QR codes for the counter, window stickers, receipts and flyers.' },
      { icon: 'globe', title: 'All your channels', text: 'Website, WhatsApp, Instagram, Facebook, Google Maps — one page.' },
      { icon: 'mail', title: 'Customer list', text: 'Collect emails and announce offers and events.' },
      { icon: 'chart', title: 'Know what works', text: 'See whether customers click your menu, booking or order link most.' },
      { icon: 'palette', title: 'On-brand', text: 'Match your brand colours with custom themes and accents.' },
    ],
    example: { username: 'influencer5', label: 'See a live business page' },
    faqs: [
      { q: 'Do I need a website?', a: 'No — for many small businesses a LinkMonkey page covers it: menu, hours, booking, order links and socials in one mobile page.' },
      { q: 'Can customers reach us on WhatsApp?', a: 'Yes, add a WhatsApp link with its real brand icon and customers message you in one tap.' },
      { q: 'How much is it?', a: 'Free to start. Pro is €4/mo or €3.33/mo billed yearly.' },
    ],
    cta: { headline: 'Your business, one scan away.', sub: 'Set it up before your next customer walks in.' },
  },
  {
    slug: 'features/tip-jar',
    title: 'Tip Jar for Creators — LinkMonkey',
    metaDescription: 'Add a tip jar to your link-in-bio page. Fans pay directly to your PayPal — LinkMonkey takes no commission. Track tips automatically.',
    eyebrow: 'Feature · Tip jar',
    h1: ['Get paid on day one.', 'Keep 100%.'],
    sub: 'The LinkMonkey tip jar sends fan support directly to your PayPal. No middleman, no commission, automatic tracking of every tip.',
    bullets: ['Direct PayPal payouts', '0% commission', 'Auto-tracked tips', 'Custom amounts'],
    features: [
      { icon: 'heart', title: 'Direct to your PayPal', text: 'Tips go from fan to you. LinkMonkey never touches the money.' },
      { icon: 'euro', title: 'No commission', text: 'Unlike most platforms, we take 0% of your tips.' },
      { icon: 'chart', title: 'Automatic tracking', text: 'Every tip is logged in your dashboard automatically.' },
      { icon: 'palette', title: 'Fits your page', text: 'The tip widget adopts your theme, accent and currency.' },
    ],
    faqs: [
      { q: 'Does LinkMonkey take a cut of tips?', a: 'No. Tips are paid directly to your PayPal — LinkMonkey takes 0% commission. Standard PayPal fees may apply.' },
      { q: 'How do I set up the tip jar?', a: 'Add your PayPal details in Settings, toggle the tip jar on, and it appears on your public page.' },
      { q: 'Can I see who tipped?', a: 'Yes, tips are tracked automatically in your dashboard.' },
    ],
    cta: { headline: 'Turn followers into supporters.', sub: 'Your tip jar can be live in five minutes.' },
  },
  {
    slug: 'features/qr-codes',
    title: 'QR Code Generator for Your Bio Link — LinkMonkey',
    metaDescription: 'Generate branded QR codes for your link-in-bio page. PNG and SVG downloads for flyers, merch, gig posters, counters and business cards.',
    eyebrow: 'Feature · QR codes',
    h1: ['Your page, offline.', 'One scan away.'],
    sub: 'Generate a branded QR code for your LinkMonkey page and put it on flyers, merch, posters, receipts and business cards. PNG and SVG downloads included.',
    bullets: ['PNG + SVG downloads', 'Brand colours', 'Print-ready', 'Included with Pro'],
    features: [
      { icon: 'qrcode', title: 'Print-ready formats', text: 'Download PNG for screens and SVG for crisp print at any size.' },
      { icon: 'palette', title: 'Brand colours', text: 'Match the QR to your accent colour and style.' },
      { icon: 'chart', title: 'Track offline traffic', text: 'Scans land on your page and show up in your analytics.' },
      { icon: 'zap', title: 'Instant generation', text: 'One click in your dashboard — no third-party tools.' },
    ],
    faqs: [
      { q: 'What formats can I download?', a: 'PNG for digital use and SVG for print at any size without quality loss.' },
      { q: 'Where should I use my QR code?', a: 'Gig posters, merch tables, business cards, café counters, product packaging, receipts — anywhere offline people can scan to reach all your links.' },
      { q: 'Is the QR code generator free?', a: 'QR codes are part of LinkMonkey Pro — €4/mo or €3.33/mo billed yearly, with a 7-day free trial.' },
    ],
    cta: { headline: 'Bridge offline to online.', sub: 'Print it once, update your links forever.' },
  },
  {
    slug: 'features/analytics',
    title: 'Link in Bio Analytics & Visitor Map — LinkMonkey',
    metaDescription: 'See who visits your bio link: clicks per link, page views, and a country-level visitor map. Analytics included with LinkMonkey Pro.',
    eyebrow: 'Feature · Analytics',
    h1: ['Know your audience.', 'Down to the country.'],
    sub: 'LinkMonkey analytics shows page views, clicks per link, click-through rates and a country-level visitor map — so you know what works and where your fans are.',
    bullets: ['Clicks per link', 'Visitor country map', 'View trends', 'No extra cost'],
    features: [
      { icon: 'chart', title: 'Clicks per link', text: 'See exactly which links your audience taps, ranked.' },
      { icon: 'globe', title: 'Visitor map', text: 'A country-level map of where your visitors come from.' },
      { icon: 'zap', title: 'Live view counts', text: 'Watch views roll in on your dashboard in real time.' },
      { icon: 'calendar', title: 'Trends over time', text: 'Spot the spike after every post, drop and stream.' },
    ],
    faqs: [
      { q: 'What analytics does LinkMonkey provide?', a: 'Page views, clicks per link, click-through rates and a country-level map of visitors.' },
      { q: 'Do I need to install anything?', a: 'No — analytics are collected automatically on your public page, privacy-friendly with no third-party trackers.' },
      { q: 'Is analytics extra?', a: 'No, full analytics is part of Pro (€4/mo or €3.33/mo billed yearly) — not a separate add-on.' },
    ],
    cta: { headline: 'Stop guessing what works.', sub: 'Your audience is telling you — start listening.' },
  },
  {
    slug: 'features/rich-embeds',
    title: 'Rich Embeds for Your Bio Link — LinkMonkey',
    metaDescription: 'Embed YouTube, Spotify, SoundCloud, Apple Music, TikTok and more directly in your link-in-bio page. Auto-detected inline players.',
    eyebrow: 'Feature · Rich embeds',
    h1: ['Don\u2019t link your content.', 'Play it.'],
    sub: 'Paste a YouTube, Spotify, SoundCloud, Apple Music, Bandcamp or TikTok link and LinkMonkey turns it into an inline player automatically. Fans watch and listen without leaving your page.',
    bullets: ['Auto-detected', 'Inline players', '15+ platforms', 'Toggle per link'],
    features: [
      { icon: 'music', title: 'Music platforms', text: 'Spotify, SoundCloud, Apple Music, Bandcamp, Audiomack, Deezer, Tidal, Mixcloud.' },
      { icon: 'zap', title: 'Video platforms', text: 'YouTube, TikTok, Twitch, Vimeo, Dailymotion, Loom and more.' },
      { icon: 'sparkles', title: 'Zero setup', text: 'Paste the link — the player appears automatically. Toggle embeds per link.' },
      { icon: 'palette', title: 'Matches your theme', text: 'Players sit inside cards styled to your theme and accent.' },
    ],
    faqs: [
      { q: 'Which platforms can I embed?', a: 'YouTube, Spotify, SoundCloud, Apple Music, Bandcamp, TikTok, Twitch, Vimeo, Audiomack, Deezer, Tidal, Mixcloud and more — over 15 platforms auto-detected.' },
      { q: 'Do embeds slow my page down?', a: 'No, players are lazy-loaded so your page stays fast.' },
      { q: 'Can I turn an embed off?', a: 'Yes, every link has an embed toggle — off means it renders as a normal button.' },
    ],
    cta: { headline: 'Turn your page into a player.', sub: 'Your content, playing where your fans already are.' },
  },
  {
    slug: 'beacons-alternative',
    title: 'LinkMonkey — A Simple, Affordable Beacons Alternative',
    metaDescription: 'Looking for a Beacons alternative? LinkMonkey keeps it simple: one Pro plan from €3.33/mo with embeds, tip jar, analytics and QR codes — no upsells.',
    eyebrow: 'Beacons alternative',
    h1: ['All the essentials.', 'None of the upsells.'],
    sub: 'Beacons bundles a dozen tools with per-feature pricing. LinkMonkey does the link-in-bio part brilliantly, with everything included in one simple plan from €3.33/mo.',
    bullets: ['One plan, all features', 'No per-feature upsells', '0% commission tips', 'Free plan forever'],
    features: CORE_FEATURES,
    comparison: {
      competitor: 'Beacons',
      rows: [
        { label: 'Free plan', us: 'Yes — forever', them: 'Yes' },
        { label: 'Pricing model', us: 'One Pro plan · €3.33/mo billed yearly', them: 'Multiple paid add-ons & tiers' },
        { label: 'Tips commission', us: '0% — direct PayPal', them: 'Fee on free plan' },
        { label: 'Rich embeds', us: 'Included', them: 'Included' },
        { label: 'QR codes (PNG + SVG)', us: 'Included', them: 'Limited' },
        { label: 'Fan page feed', us: 'Included', them: 'Not available' },
        { label: 'Focus', us: 'Link in bio, done properly', them: 'Many bundled tools' },
      ],
    },
    faqs: [
      { q: 'How is LinkMonkey different from Beacons?', a: 'Beacons bundles many tools (email, store, invoicing) with tiered pricing. LinkMonkey focuses on the link-in-bio page itself and includes everything — embeds, tips, analytics, QR codes — in one plan from €3.33/mo.' },
      { q: 'Does LinkMonkey take a cut of sales or tips?', a: 'No. Tips go directly to your PayPal with 0% commission.' },
      { q: 'Can I switch from Beacons easily?', a: 'Yes — recreate your links in minutes and swap the URL in your bios.' },
    ],
    cta: { headline: 'Simple beats bundled.', sub: 'One page, one plan, everything included.' },
  },
  {
    slug: 'carrd-alternative',
    title: 'LinkMonkey — The Creator-Focused Carrd Alternative',
    metaDescription: 'Carrd is great for one-page sites — LinkMonkey is built for creators: bio link, embeds, tip jar, analytics and QR codes with zero building required.',
    eyebrow: 'Carrd alternative',
    h1: ['No building.', 'Just your page.'],
    sub: 'Carrd gives you a blank canvas. LinkMonkey gives you a finished, beautiful creator page in two minutes — with embeds, tips, analytics and QR codes built in.',
    bullets: ['No site builder needed', 'Live in 2 minutes', 'Creator features built in', 'Free plan forever'],
    features: CORE_FEATURES,
    comparison: {
      competitor: 'Carrd',
      rows: [
        { label: 'Setup time', us: '2 minutes — no builder', them: 'Build it yourself' },
        { label: 'Rich embeds', us: 'Auto-detected players', them: 'Manual embed codes (Pro)' },
        { label: 'Tip jar', us: 'Built in, 0% commission', them: 'DIY integration' },
        { label: 'Analytics + visitor map', us: 'Built in', them: 'Third-party required' },
        { label: 'QR code generator', us: 'Built in', them: 'Not included' },
        { label: 'Timed links (auto show/hide)', us: 'Built in', them: 'Not included' },
        { label: 'Best for', us: 'Creators & link in bio', them: 'General one-page sites' },
      ],
    },
    faqs: [
      { q: 'Is LinkMonkey easier than Carrd?', a: 'For a bio link, yes — there is nothing to build. Add links, pick a theme, done. Carrd is a general site builder; LinkMonkey is purpose-built for creators.' },
      { q: 'Can I still customise my page?', a: 'Yes — 17 themes, 12 fonts, 11 wallpapers, animations, button styles and custom accent colours.' },
      { q: 'What does it cost?', a: 'Free to start; Pro is €4/mo or €3.33/mo billed yearly.' },
    ],
    cta: { headline: 'Skip the site builder.', sub: 'Your page is ready — just add links.' },
  },
  {
    slug: 'stan-store-alternative',
    title: 'LinkMonkey — An Affordable Stan Store Alternative',
    metaDescription: 'Stan Store costs $29/mo. LinkMonkey gives creators a beautiful bio link with tips, embeds, analytics and QR codes from €3.33/mo.',
    eyebrow: 'Stan Store alternative',
    h1: ['€3.33 a month.', 'Not $29.'],
    sub: 'Stan Store starts around $29/mo. If you want a beautiful creator page with direct fan support, embeds, analytics and QR codes — LinkMonkey does it from €3.33/mo.',
    bullets: ['~10x cheaper', '0% commission tips', 'Free plan forever', 'All core features included'],
    features: CORE_FEATURES,
    comparison: {
      competitor: 'Stan Store',
      rows: [
        { label: 'Starting price', us: 'Free · Pro from €3.33/mo', them: 'From ~$29/mo' },
        { label: 'Free plan', us: 'Yes — forever', them: 'Trial only' },
        { label: 'Fan support / tips', us: 'Direct PayPal, 0% commission', them: 'Checkout with fees' },
        { label: 'Rich embeds', us: 'Included', them: 'Limited' },
        { label: 'Analytics + visitor map', us: 'Included', them: 'Included' },
        { label: 'QR code generator', us: 'Included', them: 'Not core' },
        { label: 'Best for', us: 'Creators who want a great bio link', them: 'Selling digital products' },
      ],
    },
    faqs: [
      { q: 'Is LinkMonkey a full Stan Store replacement?', a: 'If you sell heavy digital product funnels, Stan Store is built for that. If you want a great creator page with direct tips, embeds and analytics at a fraction of the price, LinkMonkey is the better fit.' },
      { q: 'How do I get paid on LinkMonkey?', a: 'The tip jar pays directly to your PayPal with 0% commission from LinkMonkey.' },
      { q: 'What does LinkMonkey cost?', a: 'Free to start; Pro is €4/mo or €40/yr (€3.33/mo) with a 7-day free trial.' },
    ],
    cta: { headline: 'Keep the features. Lose the price tag.', sub: 'Everything you need from €3.33/mo.' },
  },
];

export const getSeoPage = (slug) => seoPages.find((p) => p.slug === slug);
